import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { CLIENT_STATUSES, CLIENT_TYPES } from '@stoplord/shared';
import { clientsApi } from '@/services/crm';
import type { Client } from '@/services/types';
import { toast } from '@/store/ui';
import { useTerms } from '@/hooks/usePermission';
import { humanize } from '@/lib/format';
import { Button, Checkbox, Field, Input, Modal, Select, Textarea } from '@/components/ui';
import { UserSelect } from '../shared/UserPicker';
import { compact, handleFormError } from '../shared/forms';

const optEmail = z.union([z.literal(''), z.string().email('Invalid email')]).optional();
const schema = z.object({
  name: z.string().trim().min(1, 'Required').max(160),
  type: z.enum(CLIENT_TYPES),
  status: z.enum(CLIENT_STATUSES),
  email: optEmail,
  phone: z.string().optional(),
  website: z.string().optional(),
  gstin: z.union([z.literal(''), z.string().regex(/^[0-9A-Za-z]{15}$/, 'GSTIN must be 15 characters')]).optional(),
  category: z.string().optional(),
  relationshipManagerId: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  notes: z.string().optional(),
  contacts: z.array(z.object({ name: z.string().min(1, 'Name required'), designation: z.string().optional(), email: optEmail, phone: z.string().optional(), isPrimary: z.boolean() })).max(20),
});
type Form = z.infer<typeof schema>;

export function ClientFormModal({ open, onClose, client }: { open: boolean; onClose: () => void; client?: Client }) {
  const terms = useTerms();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const editing = !!client;
  const { register, handleSubmit, reset, control, formState, setError } = useForm<Form>({ resolver: zodResolver(schema) });
  const contacts = useFieldArray({ control, name: 'contacts' });
  useEffect(() => {
    if (!open) return;
    reset({
      name: client?.name ?? '',
      type: client?.type ?? 'company',
      status: client?.status ?? 'active',
      email: client?.email ?? '',
      phone: client?.phone ?? '',
      website: client?.website ?? '',
      gstin: client?.gstin ?? '',
      category: client?.category ?? '',
      relationshipManagerId: client?.relationshipManagerId ?? '',
      city: client?.billingAddress?.city ?? '',
      state: client?.billingAddress?.state ?? '',
      notes: client?.notes ?? '',
      contacts: client?.contacts.map((c) => ({ name: c.name, designation: c.designation ?? '', email: c.email ?? '', phone: c.phone ?? '', isPrimary: c.isPrimary })) ?? [],
    });
  }, [open, client, reset]);

  const mut = useMutation({
    mutationFn: (v: Form) => {
      const body = compact({
        name: v.name, type: v.type, status: v.status, email: v.email, phone: v.phone, website: v.website, gstin: v.gstin, category: v.category, notes: v.notes,
        relationshipManagerId: v.relationshipManagerId,
        billingAddress: { city: v.city || null, state: v.state || null },
        contacts: v.contacts.map((c) => compact(c)),
      });
      return editing ? clientsApi.update(client!._id, body) : clientsApi.create(body);
    },
    onSuccess: (c) => {
      void qc.invalidateQueries({ queryKey: ['clients'] });
      toast.success(editing ? `${terms.client.singular} updated` : `${terms.client.singular} created`, c.clientNumber);
      onClose();
      if (!editing) navigate(`/crm/clients/${c._id}`);
    },
  });

  return (
    <Modal open={open} onClose={onClose} size="lg" title={editing ? `Edit ${client!.clientNumber}` : `New ${terms.client.singular.toLowerCase()}`} footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button loading={mut.isPending} onClick={handleSubmit((v) => mut.mutateAsync(v).catch((e) => handleFormError(e, setError)))}>{editing ? 'Save changes' : 'Create'}</Button></>}>
      <form className="grid gap-4 sm:grid-cols-2" noValidate onSubmit={(e) => e.preventDefault()}>
        <Field label="Name" required error={formState.errors.name?.message}>{(p) => <Input {...p} {...register('name')} />}</Field>
        <Field label="Type">{(p) => <Select {...p} {...register('type')}>{CLIENT_TYPES.map((t) => <option key={t} value={t}>{humanize(t)}</option>)}</Select>}</Field>
        <Field label="Email" error={formState.errors.email?.message}>{(p) => <Input {...p} type="email" {...register('email')} />}</Field>
        <Field label="Phone" error={formState.errors.phone?.message}>{(p) => <Input {...p} type="tel" {...register('phone')} />}</Field>
        <Field label="Website">{(p) => <Input {...p} {...register('website')} />}</Field>
        <Field label="GSTIN" error={formState.errors.gstin?.message} hint="Optional · 15 characters">{(p) => <Input {...p} {...register('gstin')} />}</Field>
        <Field label="City">{(p) => <Input {...p} {...register('city')} />}</Field>
        <Field label="State">{(p) => <Input {...p} {...register('state')} />}</Field>
        <Field label="Category">{(p) => <Input {...p} {...register('category')} />}</Field>
        <Field label="Status">{(p) => <Select {...p} {...register('status')}>{CLIENT_STATUSES.map((t) => <option key={t} value={t}>{humanize(t)}</option>)}</Select>}</Field>
        <Field label="Relationship manager" className="sm:col-span-2" hint="Defaults to you">{(p) => <Controller control={control} name="relationshipManagerId" render={({ field }) => <UserSelect id={p.id} value={field.value ?? ''} onChange={field.onChange} placeholder="Me" />} />}</Field>
        <div className="sm:col-span-2">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-sm font-medium">Contact persons</p>
            <Button size="sm" variant="soft" icon={<Plus className="h-4 w-4" />} disabled={contacts.fields.length >= 20} onClick={() => contacts.append({ name: '', designation: '', email: '', phone: '', isPrimary: contacts.fields.length === 0 })}>Add contact</Button>
          </div>
          <div className="space-y-3">
            {contacts.fields.map((f, i) => (
              <div key={f.id} className="grid gap-2 rounded-lg border border-line p-3 sm:grid-cols-[1fr_1fr_1fr_1fr_auto]">
                <Input aria-label="Contact name" placeholder="Name" {...register(`contacts.${i}.name`)} aria-invalid={!!formState.errors.contacts?.[i]?.name} />
                <Input aria-label="Designation" placeholder="Designation" {...register(`contacts.${i}.designation`)} />
                <Input aria-label="Email" placeholder="Email" {...register(`contacts.${i}.email`)} />
                <Input aria-label="Phone" placeholder="Phone" {...register(`contacts.${i}.phone`)} />
                <div className="flex items-center gap-2">
                  <Checkbox label="Primary" {...register(`contacts.${i}.isPrimary`)} />
                  <Button variant="ghost" size="icon" aria-label="Remove contact" onClick={() => contacts.remove(i)}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            ))}
          </div>
        </div>
        <Field label="Notes" className="sm:col-span-2">{(p) => <Textarea {...p} rows={3} {...register('notes')} />}</Field>
      </form>
    </Modal>
  );
}
